import { requirePermission } from "@/lib/casl/route-guards";
import { DataTable } from "@/components/data-table/data-table";
import { columns } from "@/components/mentor-payout/columns";
import { Stats } from "@/components/mentor-payout/stats";
import { DashboardStats, PayoutData, useGetMentorPayouts } from "@/lib/api/mentor-scoring";
import { useDataTable } from "@/lib/hooks/use-data-table";
import { createFileRoute, useRouter, useSearch } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { Card, CardContent } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { MonthPickerMentorPayout, getCurrentMonthNumber } from "@/components/ui/month-picker-mentor-payout";

export const Route = createFileRoute("/_authenticated/mentor-payout/")({
  component: MentorScoring,
  beforeLoad: ({ context }) => {
    requirePermission(context.user, "read", "payouts");
  },
});

// Helper function to convert sequential month number to actual month/year
const getActualMonthYear = (sequentialMonth: number) => {
  const startYear = 2025;
  
  const totalMonths = sequentialMonth;
  const actualYear = startYear + Math.floor(totalMonths / 12);
  const actualMonth = totalMonths % 12;
  
  return {
    year: actualYear.toString(),
    month: String(actualMonth + 1).padStart(2, "0") // Convert 0-11 to 01-12
  };
};

function MentorScoring() {
  const [showAddUser, setShowAddUser] = useState(false);
  const [rowInfo, setRowInfo] = useState<PayoutData | undefined>();
  const [selectedMonth, setSelectedMonth] = useState<number>(getCurrentMonthNumber()); // Default to current month
  const router = useRouter();
  const { 
    page, 
    per_page, 
    search, 
  } = useSearch({
    strict: false,
  }) as Record<string, string>;

  const currentPage = parseInt(page || "1", 10);
  const pageSize = parseInt(per_page || "20", 10);
  
  // Convert sequential month to actual year and month
  const { year, month } = getActualMonthYear(selectedMonth);

  const { data: payoutData, isPending } = useGetMentorPayouts(year, month);

  // Filter and paginate data locally
  const filteredData = useMemo(() => {
    if (!payoutData) return [];
    
    let filtered = payoutData;
    
    // Apply search filter
    if (search) {
      filtered = filtered.filter(item => 
        item.name.toLowerCase().includes(search.toLowerCase())
      );
    }
    
    return filtered;
  }, [payoutData, search]);

  // Calculate pagination
  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  const { table } = useDataTable({
    columns,
    data: paginatedData,
    defaultPerPage: pageSize,
    pageCount: totalPages,
    filterFields: [],
  });

  const handlePageChange = (newPageIndex: number) => {
    const newPage = newPageIndex + 1;
    router.navigate({
      to: router.state.location.pathname,
      search: (prev: any) => ({
        ...prev,
        page: newPage,
      }),
    });
  };

  const handlePageSizeChange = (newPageSize: number) => {
    router.navigate({
      to: router.state.location.pathname,
      search: (prev: any) => ({
        ...prev,
        page: 1,
        per_page: newPageSize,
      }),
    });
  };

  // Calculate dashboard stats from payout data
  const dashstat: DashboardStats = useMemo(() => {
    if (!payoutData) {
      return {
        totalMentors: 0,
        totalBonusPayouts: 0,
        avgBonusPerMentor: 0,
        totalPaidEngagements: 0,
      };
    }

    const totalMentors = payoutData.length;
    const totalBonusPayouts = payoutData.reduce((sum, item) => {
      const bonus = typeof item.current_financial_bonus === 'string' 
        ? parseFloat(item.current_financial_bonus) || 0 
        : item.current_financial_bonus || 0;
      return sum + bonus;
    }, 0);
    const totalPaidEngagements = payoutData.reduce((sum, item) => sum + Math.max(0, item.total_bonus_teens - 20), 0);
    const avgBonusPerMentor = totalMentors > 0 ? totalBonusPayouts / totalMentors : 0;

    return {
      totalMentors,
      totalBonusPayouts,
      avgBonusPerMentor,
      totalPaidEngagements,
    };
  }, [payoutData]);


  const handleMonthChange = (month: number) => {
    setSelectedMonth(month);
    router.navigate({
      search: ((old: any) => ({
        ...old,
        selectedMonth: month.toString(),
        page: "1",
      })) as any,
    });
  };


  return (
    <div className="m-6 pb-16">
      <div className="flex flex-row">
        <h2 className="my-6 text-2xl font-bold leading-7 text-black">
          Mentor Payout Overview
        </h2>
        <TooltipProvider key="beta-tag">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge
                      variant="secondary"
                      className="cursor-pointer bg-blue-100 text-blue-800 hover:bg-blue-200 ml-5 my-5"
                    >
                      Beta
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="w-64">
                      This feature is currently in beta, please carefully monitor
                      the following data.
                    </p>
                  </TooltipContent>
                </Tooltip>
        </TooltipProvider>
      </div>
      <Stats data={dashstat} />

      
      <div className="flex items-center gap-2 my-4">
        <span className="text-sm font-medium">Month:</span>
        <MonthPickerMentorPayout
          month={selectedMonth}
          setMonth={handleMonthChange}
          className="w-fit min-w-[160px]"
        />
      </div>


      <DataTable
        table={table}
        getCanNextPage={() => {
          return currentPage < totalPages;
        }}
        getCanPreviousPage={() => {
          return currentPage > 1;
        }}
        onRowClick={(row) => {
          setRowInfo(row);
          setShowAddUser(true);
        }}
        isPending={isPending}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      >
      </DataTable>
      <Dialog open={showAddUser} onOpenChange={setShowAddUser}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{rowInfo?.name}</DialogTitle>
          </DialogHeader>
          <Card>
            <CardContent className="p-1 text-sm">
              <div className="bg-white rounded-lg p-6 shadow-sm max-w-md mx-auto">
                <div className="grid grid-cols-2 gap-6">
                    <div className="flex flex-col">
                        <div className="text-sm text-gray-600 mb-1">Total Bonus</div>
                        <div className="text-2xl font-semibold text-gray-900">${
                          rowInfo ? (typeof rowInfo.current_financial_bonus === 'string' 
                            ? parseFloat(rowInfo.current_financial_bonus) || 0 
                            : rowInfo.current_financial_bonus || 0).toFixed(2) : '0.00'
                        }</div>
                    </div>
                    
                    <div className="flex flex-col">
                        <div className="text-sm text-gray-600 mb-1">Paid Engagements</div>
                        <div className="text-2xl font-semibold text-gray-900">{
                          rowInfo ? Math.max(0, rowInfo.total_bonus_teens - 20) : 0
                        }</div>
                    </div>
                    
                    <div className="flex flex-col">
                        <div className="text-sm text-gray-600 mb-1">Total Engaged Teens</div>
                        <div className="text-m font-semibold text-gray-900">{rowInfo?.total_bonus_teens || 0}</div>
                    </div>
                    
                    <div className="flex flex-col">
                        <div className="text-sm text-gray-600 mb-1">Engagement Window</div>
                        <div className="text-m font-semibold text-gray-900">{`${month} ${year}`}</div>
                    </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </DialogContent>
      </Dialog>
    </div>
  );
}


